// SettingsFrame.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Checkbox,
  InputAdornment,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { DataGrid, GridFooterContainer } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSettings,
  addKeyword,
  deleteKeywords,
  activateKeywords,
  deactivateKeywords,
  addNhs,
  deleteNhs,
  activateNhs,
  deactivateNhs,
  clearError,
  clearSuccess,
} from "../reducers/settingsSlice";

const CompactHeader = ({ children }) => (
  <Box
    sx={{
      textAlign: "center",
      lineHeight: 1.5,
      fontSize: "0.72rem",
      fontWeight: 500,
      whiteSpace: "normal",
    }}
  >
    {children}
  </Box>
);

function TabPanel({ children, value, index, sx, ...other }) {
  const active = value === index;
  return (
    <div
      role="tabpanel"
      hidden={!active}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      style={{
        display: active ? "flex" : "none",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
      {...other}
    >
      {active && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            ...sx,
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

// Replaces the paginated footer with a simple "showing X of Y" readout,
// since pagination is dropped in favour of native grid scrolling.
function CountFooter({ shown, total, noun }) {
  return (
    <GridFooterContainer
      sx={{ px: 1.5, py: 0.5, minHeight: 32, justifyContent: "flex-start" }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {shown === total
          ? `${total} ${noun}${total === 1 ? "" : "s"}`
          : `Showing ${shown} of ${total} ${noun}${total === 1 ? "" : "s"}`}
      </Typography>
    </GridFooterContainer>
  );
}

const SettingsFrame = () => {
  const dispatch = useDispatch();
  const { keywords, nhsNumbers, error, success } = useSelector(
    (s) => s.settings || {},
  );
  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );
  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  const [tab, setTab] = useState(0);
  const [newKeyword, setNewKeyword] = useState("");
  const [newNhs, setNewNhs] = useState("");
  const [newNhsReason, setNewNhsReason] = useState("");

  const [keywordSearch, setKeywordSearch] = useState("");
  const [nhsSearch, setNhsSearch] = useState("");

  const [selectedKeywords, setSelectedKeywords] = useState({
    ids: new Set(),
    type: "include",
  });

  const [selectedNhs, setSelectedNhs] = useState({
    ids: new Set(),
    type: "include",
  });

  // Load settings
  useEffect(() => {
    if (license_key) dispatch(fetchSettings({ license_key }));
  }, [dispatch, license_key]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => dispatch(clearSuccess()), 3000);
      return () => clearTimeout(t);
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => dispatch(clearError()), 3000);
      return () => clearTimeout(t);
    }
  }, [error, dispatch]);

  // Keyword rows
  const keywordRows = useMemo(
    () =>
      (keywords || []).map((k, i) => ({
        id: `kw-${i}`,
        keyword: k.keyword,
        enabled: k.enabled,
      })),
    [keywords],
  );

  const filteredKeywordRows = useMemo(() => {
    const q = keywordSearch.trim().toLowerCase();
    if (!q) return keywordRows;
    return keywordRows.filter((r) =>
      (r.keyword || "").toLowerCase().includes(q),
    );
  }, [keywordRows, keywordSearch]);

  // NHS rows
  const nhsRows = useMemo(
    () =>
      (nhsNumbers || []).map((n, i) => ({
        id: `nhs-${i}`,
        nhs_number: n.nhs_number,
        reason: n.reason || "",
        enabled: n.enabled,
      })),
    [nhsNumbers],
  );

  const filteredNhsRows = useMemo(() => {
    const q = nhsSearch.trim().toLowerCase();
    if (!q) return nhsRows;
    return nhsRows.filter(
      (r) =>
        (r.nhs_number || "").toLowerCase().includes(q) ||
        (r.reason || "").toLowerCase().includes(q),
    );
  }, [nhsRows, nhsSearch]);

  // Keyword columns
  const keywordColumns = [
    {
      field: "keyword",
      headerName: "Keyword",
      flex: 6,
      cellClassName: "bg-grey",
    },
    {
      field: "enabled",
      flex: 1.3,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-grey",
      renderHeader: () => <CompactHeader>Enabled</CompactHeader>,
      renderCell: (params) => (
        <Checkbox
          size="small"
          checked={params.row.enabled}
          disabled={!has_write}
          onClick={(event) => event.stopPropagation()}
          onChange={async (e) => {
            const kw = params.row.keyword;
            if (e.target.checked)
              await dispatch(activateKeywords({ keywords: [kw], license_key }));
            else
              await dispatch(
                deactivateKeywords({ keywords: [kw], license_key }),
              );
          }}
        />
      ),
    },
    {
      field: "actions",
      flex: 1.3,
      align: "center",
      headerAlign: "center",
      sortable: false,
      cellClassName: "bg-grey",
      renderHeader: () => <CompactHeader>Action</CompactHeader>,
      renderCell: (params) => (
        <Tooltip title="Delete">
          <span>
            <IconButton
              size="small"
              disabled={!has_write}
              onClick={async () => {
                await dispatch(
                  deleteKeywords({
                    keywords: [params.row.keyword],
                    license_key,
                  }),
                );
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  // NHS columns
  const nhsColumns = [
    {
      field: "nhs_number",
      headerName: "NHS Number",
      flex: 3,
      cellClassName: "bg-grey",
    },
    {
      field: "reason",
      flex: 5,
      cellClassName: "bg-grey",
      renderHeader: () => <CompactHeader>Reason</CompactHeader>,
    },
    {
      field: "enabled",
      flex: 1.3,
      align: "center",
      headerAlign: "center",
      cellClassName: "bg-grey",
      renderHeader: () => <CompactHeader>Enabled</CompactHeader>,
      renderCell: (params) => (
        <Checkbox
          size="small"
          checked={params.row.enabled}
          disabled={!has_write}
          onClick={(event) => event.stopPropagation()}
          onChange={async (e) => {
            const n = params.row.nhs_number;
            if (e.target.checked)
              await dispatch(activateNhs({ nhs_numbers: [n], license_key }));
            else
              await dispatch(deactivateNhs({ nhs_numbers: [n], license_key }));
          }}
        />
      ),
    },
    {
      field: "actions",
      flex: 1.3,
      align: "center",
      headerAlign: "center",
      sortable: false,
      cellClassName: "bg-grey",
      renderHeader: () => <CompactHeader>Action</CompactHeader>,
      renderCell: (params) => (
        <Tooltip title="Delete">
          <span>
            <IconButton
              size="small"
              disabled={!has_write}
              onClick={async () => {
                await dispatch(
                  deleteNhs({
                    nhs_numbers: [params.row.nhs_number],
                    license_key,
                  }),
                );
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box
      sx={{
        height: "calc(100vh - 50px)",
        display: "flex",
        flexDirection: "column",
        ml: -1,
        mr: -1,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "none",
          border: "none",
          borderRadius: 0,
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          aria-label="settings tabs"
          sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5 } }}
        >
          <Tab label="Inconclusive Keywords" id="settings-tab-0" />
          <Tab label="Skip Patients" id="settings-tab-1" />
        </Tabs>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Keywords */}
          <TabPanel value={tab} index={0}>
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                mb: 0.5,
                mt: 0.5,
                borderRadius: 2,
                borderColor: "#cbd5e0",
                background: "#fafafa",
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <TextField
                  label="Search Keyword"
                  value={keywordSearch}
                  onChange={(e) => setKeywordSearch(e.target.value)}
                  size="small"
                  sx={{
                    minWidth: 240,
                    "& .MuiInputBase-root": { fontSize: "0.8rem" },
                    "& .MuiInputLabel-root": {
                      backgroundColor: "#fff",
                      px: 0.5,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: keywordSearch ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setKeywordSearch("")}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />

                <TextField
                  label="Add keyword"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Add a keyword"
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": { fontSize: "0.8rem" },
                    "& .MuiInputLabel-root": {
                      backgroundColor: "#fff",
                      px: 0.5,
                    },
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newKeyword.trim()) {
                      dispatch(
                        addKeyword({ keyword: newKeyword.trim(), license_key }),
                      );
                      setNewKeyword("");
                    }
                  }}
                />
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon fontSize="small" />}
                  disabled={!has_write || !newKeyword.trim()}
                  onClick={() => {
                    if (!newKeyword.trim()) return;
                    dispatch(
                      addKeyword({ keyword: newKeyword.trim(), license_key }),
                    );
                    setNewKeyword("");
                  }}
                  sx={{ textTransform: "none", py: 0.25, minHeight: 26 }}
                >
                  Add
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  disabled={
                    !has_write ||
                    !(selectedKeywords.ids && selectedKeywords.ids.size > 0)
                  }
                  onClick={async () => {
                    const selectedIds = Array.from(
                      selectedKeywords.ids || new Set(),
                    );
                    if (!selectedIds.length) return;

                    const kws = selectedIds
                      .map(
                        (id) => keywordRows.find((r) => r.id === id)?.keyword,
                      )
                      .filter(Boolean);

                    if (!kws.length) return;

                    await dispatch(
                      deleteKeywords({ keywords: kws, license_key }),
                    );

                    setSelectedKeywords({ ids: new Set(), type: "include" });
                  }}
                  sx={{ textTransform: "none", py: 0.25, minHeight: 26 }}
                >
                  Delete Selected ({selectedKeywords.ids?.size || 0})
                </Button>
              </Stack>
            </Paper>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <DataGrid
                variant="dense"
                rowHeight={26}
                rows={filteredKeywordRows}
                columns={keywordColumns}
                checkboxSelection
                disableRowSelectionOnClick
                hideFooterPagination
                hideFooterSelectedRowCount
                rowSelectionModel={selectedKeywords}
                onRowSelectionModelChange={setSelectedKeywords}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
                slots={{
                  footer: () => (
                    <CountFooter
                      shown={filteredKeywordRows.length}
                      total={keywordRows.length}
                      noun="keyword"
                    />
                  ),
                }}
                sx={{
                  height: "100%",
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": { marginBottom: "12px" },
                  "& .MuiDataGrid-row": { cursor: "default" },
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                    {
                      outline: "none",
                    },
                  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                    { outline: "none" },
                }}
              />
            </Box>
          </TabPanel>

          {/* NHS */}
          <TabPanel value={tab} index={1}>
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                mb: 0.5,
                mt: 0.5,
                borderRadius: 2,
                borderColor: "#cbd5e0",
                background: "#fafafa",
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <TextField
                  label="Search NHS Number"
                  value={nhsSearch}
                  onChange={(e) => setNhsSearch(e.target.value)}
                  size="small"
                  sx={{
                    minWidth: 240,
                    "& .MuiInputBase-root": { fontSize: "0.8rem" },

                    "& .MuiInputLabel-root": {
                      backgroundColor: "#fff",
                      px: 0.5,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: nhsSearch ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setNhsSearch("")}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />

                <TextField
                  label="NHS Number"
                  value={newNhs}
                  onChange={(e) => setNewNhs(e.target.value)}
                  size="small"
                  sx={{
                    width: 180,
                    "& .MuiInputBase-root": { fontSize: "0.8rem" },
                    "& .MuiInputLabel-root": {
                      backgroundColor: "#fff",
                      px: 0.5,
                    },
                  }}
                />
                <TextField
                  label="Reason"
                  value={newNhsReason}
                  onChange={(e) => setNewNhsReason(e.target.value)}
                  size="small"
                  sx={{
                    width: 260,
                    "& .MuiInputBase-root": { fontSize: "0.8rem" },
                    "& .MuiInputLabel-root": {
                      backgroundColor: "#fff",
                      px: 0.5,
                    },
                  }}
                />
                <Button
                  size="small"
                  variant="contained"
                  disabled={!has_write || !newNhs.trim()}
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => {
                    if (!newNhs.trim()) return;
                    dispatch(
                      addNhs({
                        nhs_number: newNhs.trim(),
                        reason: newNhsReason.trim(),
                        license_key,
                      }),
                    );
                    setNewNhs("");
                    setNewNhsReason("");
                  }}
                  sx={{ textTransform: "none", py: 0.25, minHeight: 26 }}
                >
                  Add
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  disabled={
                    !has_write || !(selectedNhs.ids && selectedNhs.ids.size > 0)
                  }
                  onClick={async () => {
                    const selectedIds = Array.from(
                      selectedNhs.ids || new Set(),
                    );
                    if (!selectedIds.length) return;
                    const nums = selectedIds
                      .map((id) => nhsRows.find((r) => r.id === id)?.nhs_number)
                      .filter(Boolean);

                    if (!nums.length) return;

                    await dispatch(
                      deleteNhs({ nhs_numbers: nums, license_key }),
                    );

                    setSelectedNhs({ ids: new Set(), type: "include" });
                  }}
                  sx={{ textTransform: "none", py: 0.25, minHeight: 26 }}
                >
                  Delete Selected ({selectedNhs.ids?.size || 0})
                </Button>
              </Stack>
            </Paper>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <DataGrid
                variant="dense"
                rowHeight={26}
                rows={filteredNhsRows}
                columns={nhsColumns}
                checkboxSelection
                disableRowSelectionOnClick
                hideFooterPagination
                hideFooterSelectedRowCount
                rowSelectionModel={selectedNhs}
                onRowSelectionModelChange={setSelectedNhs}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
                slots={{
                  footer: () => (
                    <CountFooter
                      shown={filteredNhsRows.length}
                      total={nhsRows.length}
                      noun="entry"
                    />
                  ),
                }}
                sx={{
                  height: "100%",
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": { marginBottom: "12px" },
                  "& .MuiDataGrid-row": { cursor: "default" },
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                    {
                      outline: "none",
                    },
                  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                    { outline: "none" },
                }}
              />
            </Box>
          </TabPanel>
        </Box>

        {(success || error) && (
          <Box
            sx={{
              mt: 0.5,
              mx: 0.5,
              mb: 0.5,
              px: 2,
              py: 0.75,
              borderRadius: 1,
              backgroundColor: error ? "#fdecea" : "#e8f5e9",
              color: error ? "#d32f2f" : "#2e7d32",
              border: `1px solid ${error ? "#f5c6cb" : "#c3e6cb"}`,
              fontSize: "0.85rem",
            }}
          >
            {error || success}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SettingsFrame;
